import Moment from 'moment-timezone';

export const ORDER_CUTOFF_HOUR = 7;
export const REMAINING_ORDERS_CUTOFF_HOUR = 12;

export function tenantTz(rootAccount) {
  return rootAccount && rootAccount.tenant && rootAccount.tenant.TZ;
}

export function tenantNow(tz) {
  return tz ? Moment().tz(tz) : Moment();
}

/**
 * Calendar day key for API menu/order dates (UTC midnight semantics).
 * +12h matches legacy MealsPage matching — do not use on picker clicks.
 */
export function apiMenuDayKey(date, tz) {
  let m = Moment(date);
  if (tz) {
    m = m.tz(tz);
  }
  return m.add(12, 'hours').format('YYYY-MM-DD');
}

/**
 * Calendar day key for UI picker clicks (sweet-calendar Date at local noon).
 * No +12h — the clicked wall date is already the intended day.
 */
export function pickerDayKey(date, tz) {
  let m = Moment(date);
  if (tz) {
    return m.tz(tz, true).format('YYYY-MM-DD');
  }
  return m.format('YYYY-MM-DD');
}

/** @deprecated use apiMenuDayKey or pickerDayKey explicitly */
export function menuDayKey(date, tz) {
  return apiMenuDayKey(date, tz);
}

export function isSameApiMenuDay(a, b, tz) {
  return apiMenuDayKey(a, tz) === apiMenuDayKey(b, tz);
}

export function apiAndPickerSameDay(apiDate, pickerDate, tz) {
  return apiMenuDayKey(apiDate, tz) === pickerDayKey(pickerDate, tz);
}

export function isPastMenuDay(day, tz) {
  return pickerDayKey(day, tz) < tenantNow(tz).format('YYYY-MM-DD');
}

export function isTodayMenuDay(day, tz) {
  return pickerDayKey(day, tz) === tenantNow(tz).format('YYYY-MM-DD');
}

export function isAfterOrderCutoff(day, tz, cutoffHour = ORDER_CUTOFF_HOUR) {
  if (!isTodayMenuDay(day, tz)) {
    return false;
  }
  return tenantNow(tz).hour() >= cutoffHour;
}

export function orderingClosedForDay(day, tz, cutoffHour = ORDER_CUTOFF_HOUR) {
  if (isPastMenuDay(day, tz)) {
    return true;
  }
  return isAfterOrderCutoff(day, tz, cutoffHour);
}

/** Local Date at noon for sweet-calendar events from an API date */
export function menuEventDateFromApi(date, tz) {
  const key = apiMenuDayKey(date, tz);
  const [y, mo, d] = key.split('-').map(Number);
  return new Date(y, mo - 1, d, 12, 0, 0, 0);
}

/**
 * Calendar day key for an order — API ISO dates use +12h semantics;
 * local noon Date objects from the picker use wall-date key only.
 */
export function orderCalendarDayKey(orderDate, tz) {
  if (orderDate == null || orderDate === '') {
    return '';
  }
  if (orderDate instanceof Date) {
    const pickerKey = pickerDayKey(orderDate, tz);
    const apiKey = apiMenuDayKey(orderDate, tz);
    if (pickerKey !== apiKey) {
      return pickerKey;
    }
  }
  return apiMenuDayKey(orderDate, tz);
}

/** Sweet-calendar event Date for an order (always local noon on the order day) */
export function orderCalendarEventDate(orderDate, tz) {
  const key = orderCalendarDayKey(orderDate, tz);
  const [y, mo, d] = key.split('-').map(Number);
  return new Date(y, mo - 1, d, 12, 0, 0, 0);
}

/** Order date is today or later in tenant calendar (not yet served/charged) */
export function isPendingMenuOrder(orderDate, tz) {
  return apiMenuDayKey(orderDate, tz) >= tenantNow(tz).format('YYYY-MM-DD');
}

/**
 * Orders still counting toward "remaining" balance exposure.
 * Before noon (tenant TZ): today and future. At/after noon: strictly after today.
 */
export function isRemainingMenuOrder(orderDate, tz, cutoffHour = REMAINING_ORDERS_CUTOFF_HOUR) {
  const orderKey = apiMenuDayKey(orderDate, tz);
  const todayKey = tenantNow(tz).format('YYYY-MM-DD');
  if (orderKey < todayKey) {
    return false;
  }
  if (orderKey > todayKey) {
    return true;
  }
  return tenantNow(tz).hour() < cutoffHour;
}

/** Local Date at noon from explicit Y-M-D (monthly calendar picker) */
export function pickerEventDate(year, month, day) {
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function explainOrderingUnavailable({
  menu,
  selectedDay,
  tz,
  isOrderingDisabled,
  isOrderEditDisabled,
  order,
}) {
  if (!menu || !selectedDay) {
    return null;
  }
  if (!isOrderingDisabled && !isOrderEditDisabled) {
    return null;
  }

  if (!menu.choices || menu.choices.length === 0) {
    return 'There are no meal choices available for this day.';
  }

  if (isPastMenuDay(selectedDay, tz)) {
    return 'This date has passed, so orders can no longer be placed or changed.';
  }

  if (isAfterOrderCutoff(selectedDay, tz)) {
    return 'Ordering for today closes at 7:00 AM. The window for this day has passed.';
  }

  if (order && order._id && isOrderEditDisabled) {
    return 'You already have an order for this day. Tap Edit Order on the receipt to make changes.';
  }

  return 'Ordering is not available for this day right now.';
}
